/**
 * Flow editor — local types.
 *
 * Flow is a 4-lane rhythm editor with a 3D tunnel viewport (same camera /
 * fog / vibe as the Synth Rider editor) but the notes stay glued to the
 * floor instead of floating at chest height.
 *
 * Notes are stored in raw beat time so they slot into the shared
 * rhythm-core difficulty engine without any conversion.
 */

/** Hex color per lane (green, red, yellow, blue, purple). */
export const FLOW_LANE_COLORS = ['#22C55E', '#EF4444', '#EAB308', '#3B82F6', '#A855F7'] as const;
export const FLOW_LANE_LABELS = ['Green', 'Red', 'Yellow', 'Blue', 'Purple'] as const;
/** Maximum lanes a Flow chart supports. Lane colours/labels arrays are sized to
 *  this; the ACTIVE lane count per difficulty is 3, 4 or 5 (see FlowLaneCount). */
export const FLOW_LANE_COUNT = 5;

export type FlowLaneIndex = 0 | 1 | 2 | 3 | 4;

/** A difficulty can be authored with 3, 4 or 5 lanes (KPOP FLOW mechanics use
 *  5-lane placement). Notes keep their size; only the lane distribution across
 *  the highway width changes. */
export type FlowLaneCount = 3 | 4 | 5;
export const DEFAULT_FLOW_LANE_COUNT: FlowLaneCount = 4;

/**
 * Note types supported by Flow:
 *   - 'tap'           plain note, no glyph
 *   - 'arrow-left'    directional note with a ← arrow on top
 *   - 'arrow-center'  directional note with an ↑ arrow on top
 *   - 'arrow-right'   directional note with a → arrow on top
 *
 * Trails (sustained notes) are not a separate `type`; they're any note
 * whose `duration_beats > 0`, regardless of head type. The brush selector
 * exposes 'trail' as a special placement mode that creates a head + tail.
 */
export type FlowNoteType =
  | 'tap'
  | 'arrow-left'
  | 'arrow-center'
  | 'arrow-right';

/** Convenience: all directional notes share the arrow renderer. */
export const FLOW_ARROW_TYPES = ['arrow-left', 'arrow-center', 'arrow-right'] as const;
export type FlowArrowDirection = 'left' | 'center' | 'right';

export function arrowDirectionFromType(t: FlowNoteType): FlowArrowDirection | null {
  if (t === 'arrow-left') return 'left';
  if (t === 'arrow-center') return 'center';
  if (t === 'arrow-right') return 'right';
  return null;
}

/**
 * One waypoint inside a multi-segment trail. The trail's head note IS the
 * first waypoint — its `lane` and `time_beats` must match `waypoints[0]`.
 * Subsequent waypoints may sit on different lanes, creating the zig-zag
 * trails the runtime renders as a ribbon snaking between lanes.
 */
export interface FlowTrailWaypoint {
  lane: FlowLaneIndex;
  time_beats: number;
}

export interface FlowNote {
  id: string;
  time_beats: number;
  time_seconds: number;
  lane: FlowLaneIndex;
  type: FlowNoteType;
  /** Total length of the trail in beats. For multi-waypoint trails this is
   *  derived: `waypoints[last].time_beats - waypoints[0].time_beats`. Kept
   *  as a top-level field so legacy single-lane trails (no waypoints array)
   *  still work, and so the render path can read it without branching. */
  duration_beats: number;
  /**
   * Optional list of waypoints for multi-segment trails. Includes the head
   * (waypoints[0] == {lane, time_beats}). If absent or has ≤ 1 entry the
   * note is either a tap (`duration_beats === 0`) or a single-lane trail
   * (`duration_beats > 0`, head→end on the same lane).
   */
  waypoints?: FlowTrailWaypoint[];
}

// Difficulty names are free strings so users can add custom-named difficulties.
// The 5 canonical defaults are always offered; charts may contain extra names.
export type FlowDifficultyName = string;

export const FLOW_DIFFICULTIES: FlowDifficultyName[] = ['Easy', 'Normal', 'Hard', 'Expert', 'Master'];

/** Legacy charts used 'Medium' (no 'Master'); migrate that name to 'Normal'. */
export function migrateFlowDifficultyName(name: string): string {
  return name === 'Medium' ? 'Normal' : name;
}

/** A timeline bookmark — a labelled beat position to jump back to quickly. */
export interface FlowBookmark {
  time_beats: number;
  label?: string;
}

export interface FlowChart {
  bpm: number;
  offset_seconds: number;
  /** Notes for the currently active difficulty. Always the live array the
   *  editor mutates — every reader (renderer, minimap, timeline, etc.)
   *  treats this as the source of truth for "what's on screen". */
  notes: FlowNote[];
  /** Snapshots of every NON-active difficulty's notes. The active
   *  difficulty's notes are NOT here — they live in `notes` above. When the
   *  user switches the active difficulty the store swaps `notes` with the
   *  entry from this map (creating it if missing). Older charts may load
   *  without this field; treat it as `{}` when absent. */
  difficultyNotes?: Partial<Record<FlowDifficultyName, FlowNote[]>>;
  /** Lane count (3 or 4) per difficulty. Absent entries default to 4 — older
   *  charts (no field) load as all-4, preserving their layout. */
  laneCounts?: Partial<Record<FlowDifficultyName, FlowLaneCount>>;
  meta?: {
    title?: string;
    artist?: string;
    charter?: string;
  };
  /** Timeline bookmarks (chart-wide, not per-difficulty). Older charts load
   *  without this field; treat as `[]` when absent. */
  bookmarks?: FlowBookmark[];
}

export function emptyChart(bpm: number = 120): FlowChart {
  return { bpm, offset_seconds: 0, notes: [], difficultyNotes: {}, laneCounts: {}, meta: {} };
}

/** The active lane count for a difficulty (defaults to 4 when unset). */
export function laneCountFor(
  chart: Pick<FlowChart, 'laneCounts'>,
  diff: FlowDifficultyName,
): FlowLaneCount {
  return chart.laneCounts?.[diff] ?? DEFAULT_FLOW_LANE_COUNT;
}
